import { MetaModelTriple, PARENT_REF_TYPE_NAME } from '@ardoq/api-types';

export type ParentOfEdge = {
  parentType: string;
  childType: string;
};

/**
 * Component type hierarchies, as defined by the metamodel's `'Is Parent Of'`
 * triples: a triple `{ sourceComponentType: P, referenceType: 'Is Parent Of',
 * targetComponentType: C }` means `P` is the parent of `C`.
 *
 * Every map includes the looked-up type itself, first. Types without any
 * parent-of triple have no entry - use the accessors below, which fall back to
 * the type on its own.
 *
 * - `hierarchyByType`: every type connected to the looked-up type through
 *   parent-of triples, in any direction - ancestors, descendants, siblings and
 *   cousins.
 * - `descendantsByType`: what the backend's `startSetExpansion: 'descendants'`
 *   covers, so this is the set that has real members in an expanded start set.
 * - `ancestorsByType`: the reverse walk.
 */
export type ComponentTypeHierarchies = {
  hierarchyByType: Map<string, string[]>;
  descendantsByType: Map<string, string[]>;
  ancestorsByType: Map<string, string[]>;
};

export const emptyComponentTypeHierarchies: ComponentTypeHierarchies = {
  hierarchyByType: new Map(),
  descendantsByType: new Map(),
  ancestorsByType: new Map(),
};

const isParentOf = ({ referenceType }: { referenceType: string }): boolean =>
  referenceType === PARENT_REF_TYPE_NAME;

const toParentOfEdgesFromMetaModelTriples = (
  triples: MetaModelTriple[]
): ParentOfEdge[] =>
  triples
    .filter(isParentOf)
    .map(({ sourceComponentType, targetComponentType }) => ({
      parentType: sourceComponentType,
      childType: targetComponentType,
    }));

// JSON-encode the parts so the key is unambiguous: component type names can
// contain the separator character.
const edgeKey = ({ parentType, childType }: ParentOfEdge): string =>
  JSON.stringify([parentType, childType]);

const dedupeParentOfEdges = (edges: ParentOfEdge[]): ParentOfEdge[] => {
  const seen = new Set<string>();
  return edges.filter(edge => {
    const key = edgeKey(edge);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const addToAdjacency = (
  adjacency: Map<string, string[]>,
  from: string,
  to: string
): Map<string, string[]> =>
  adjacency.set(from, [...(adjacency.get(from) ?? []), to]);

const toChildrenByParent = (edges: ParentOfEdge[]): Map<string, string[]> =>
  edges.reduce(
    (adjacency, { parentType, childType }) =>
      addToAdjacency(adjacency, parentType, childType),
    new Map<string, string[]>()
  );

const toParentsByChild = (edges: ParentOfEdge[]): Map<string, string[]> =>
  edges.reduce(
    (adjacency, { parentType, childType }) =>
      addToAdjacency(adjacency, childType, parentType),
    new Map<string, string[]>()
  );

const toNeighboursByType = (edges: ParentOfEdge[]): Map<string, string[]> =>
  edges.reduce(
    (adjacency, { parentType, childType }) =>
      addToAdjacency(
        addToAdjacency(adjacency, parentType, childType),
        childType,
        parentType
      ),
    new Map<string, string[]>()
  );

const collectReachable = (
  adjacency: Map<string, string[]>,
  from: string,
  visited: Set<string>
): string[] => {
  if (visited.has(from)) return [];
  visited.add(from);
  return [
    from,
    ...(adjacency.get(from) ?? []).flatMap(next =>
      collectReachable(adjacency, next, visited)
    ),
  ];
};

const toReachableByType = (
  types: string[],
  adjacency: Map<string, string[]>
): Map<string, string[]> =>
  new Map(
    types.map(type => [type, collectReachable(adjacency, type, new Set())])
  );

const toTypes = (edges: ParentOfEdge[]): string[] => [
  ...new Set(
    edges.flatMap(({ parentType, childType }) => [parentType, childType])
  ),
];

const toComponentTypeHierarchies = (
  edges: ParentOfEdge[]
): ComponentTypeHierarchies => {
  const parentOfEdges = dedupeParentOfEdges(edges);
  const types = toTypes(parentOfEdges);
  return {
    hierarchyByType: toReachableByType(
      types,
      toNeighboursByType(parentOfEdges)
    ),
    descendantsByType: toReachableByType(
      types,
      toChildrenByParent(parentOfEdges)
    ),
    ancestorsByType: toReachableByType(types, toParentsByChild(parentOfEdges)),
  };
};

const getHierarchyTypes = (
  { hierarchyByType }: ComponentTypeHierarchies,
  componentType: string
): string[] => hierarchyByType.get(componentType) ?? [componentType];

const getDescendantTypes = (
  { descendantsByType }: ComponentTypeHierarchies,
  componentType: string
): string[] => descendantsByType.get(componentType) ?? [componentType];

const getAncestorTypes = (
  { ancestorsByType }: ComponentTypeHierarchies,
  componentType: string
): string[] => ancestorsByType.get(componentType) ?? [componentType];

export const componentTypeHierarchyOperations = {
  toParentOfEdgesFromMetaModelTriples,
  toComponentTypeHierarchies,
  getHierarchyTypes,
  getDescendantTypes,
  getAncestorTypes,
};
